import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Plus, Send, Trash2, Pencil, Users, MailOpen, MousePointerClick, AlertTriangle, Save, FlaskConical, Eye, BookmarkPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';
import { NET_COLLECTIONS, NET_STATUSES } from './collections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGES = {
    draft: { label: 'Brouillon', cls: 'bg-stone-100 text-stone-700 border-stone-200' },
    scheduled: { label: 'Planifiée', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    sending: { label: 'Envoi en cours…', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    sent: { label: 'Envoyée', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const EMPTY_FILTERS = { collection: '', country: '', continent: '', status_relation: '', priority: '', min_score: '', tag: '', q: '' };

function AudienceBuilder({ filters, setFilters, api, lists, onSaveList, onDeleteList }) {
    const [count, setCount] = useState(null);
    const set = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setCount(null); };

    const applyList = (l) => { setFilters({ ...EMPTY_FILTERS, ...(l.filters || {}) }); setCount(null); };

    return (
        <div className="rounded-lg border border-border p-3 space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold">Audience ciblée (liste dynamique)</div>
                <div className="flex items-center gap-2">
                    {lists.length > 0 && (
                        <select className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                                value="" onChange={e => { const l = lists.find(x => x.id === e.target.value); if (l) applyList(l); }}
                                data-testid="campaign-list-select">
                            <option value="">Listes sauvegardées…</option>
                            {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                    )}
                    <Button size="sm" variant="outline" onClick={() => onSaveList(filters)} data-testid="campaign-save-list">
                        <BookmarkPlus size={12} className="mr-1" /> Sauvegarder
                    </Button>
                </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <select className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                        value={filters.collection} onChange={e => set('collection', e.target.value)} data-testid="campaign-filter-collection">
                    <option value="">Toutes collections</option>
                    {Object.entries(NET_COLLECTIONS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                </select>
                <select className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                        value={filters.status_relation} onChange={e => set('status_relation', e.target.value)}>
                    <option value="">Tous statuts</option>
                    {Object.entries(NET_STATUSES).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                </select>
                <Input className="h-9" placeholder="Pays (ex: Nigeria)" value={filters.country} onChange={e => set('country', e.target.value)} data-testid="campaign-filter-country" />
                <select className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                        value={filters.continent} onChange={e => set('continent', e.target.value)}>
                    <option value="">Tous continents</option>
                    {['Afrique', 'Europe', 'Amérique du Nord', 'Amérique du Sud', 'Asie', 'Océanie'].map(c => <option key={c}>{c}</option>)}
                </select>
                <Input className="h-9" placeholder="Priorité (ex: Très Haute)" value={filters.priority} onChange={e => set('priority', e.target.value)} />
                <Input className="h-9" type="number" placeholder="Score min" value={filters.min_score} onChange={e => set('min_score', e.target.value)} />
                <Input className="h-9" placeholder="Tag" value={filters.tag} onChange={e => set('tag', e.target.value)} />
                <Input className="h-9" placeholder="Recherche libre" value={filters.q} onChange={e => set('q', e.target.value)} />
            </div>
            <p className="text-[11px] text-foreground/50">
                Seuls les contacts avec un <strong>email</strong> et <strong>non désabonnés</strong> recevront la campagne. Les filtres sont réévalués au moment de l'envoi.
            </p>
        </div>
    );
}

function CampaignEditor({ open, onClose, onSaved, api, campaign, lists, reloadLists }) {
    const isEdit = !!campaign?.id;
    const [name, setName] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [saving, setSaving] = useState(false);
    const [audience, setAudience] = useState(null);
    const [savedId, setSavedId] = useState(null);

    useEffect(() => {
        if (open) {
            setName(campaign?.name || '');
            setSubject(campaign?.subject || '');
            setBody(campaign?.body_html || '');
            setFilters({ ...EMPTY_FILTERS, ...(campaign?.filters || {}) });
            setAudience(null);
            setSavedId(campaign?.id || null);
        }
    }, [open, campaign]);

    const cleanFilters = () => {
        const out = {};
        Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v != null) out[k] = k === 'min_score' ? Number(v) : v; });
        return out;
    };

    const save = async () => {
        if (!name.trim() || !subject.trim()) { toast.error('Nom et objet requis'); return; }
        setSaving(true);
        try {
            const payload = { name: name.trim(), subject: subject.trim(), body_html: body, filters: cleanFilters() };
            const r = savedId
                ? await api.put(`/network/campaigns/${savedId}`, payload)
                : await api.post('/network/campaigns', payload);
            setSavedId(r.data.id);
            toast.success('Campagne enregistrée');
            onSaved(r.data);
            return r.data;
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Enregistrement échoué');
            return null;
        } finally {
            setSaving(false);
        }
    };

    const checkAudience = async () => {
        const saved = await save();
        if (!saved) return;
        try {
            const r = await api.post(`/network/campaigns/${saved.id}/audience`);
            setAudience(r.data);
        } catch { toast.error('Calcul d\'audience échoué'); }
    };

    const saveList = async (f) => {
        const nm = window.prompt('Nom de la liste de diffusion :');
        if (!nm?.trim()) return;
        try {
            const out = {};
            Object.entries(f).forEach(([k, v]) => { if (v) out[k] = v; });
            await api.post('/network/lists', { name: nm.trim(), filters: out });
            toast.success('Liste sauvegardée');
            reloadLists();
        } catch { toast.error('Échec'); }
    };

    return (
        <Dialog open={open} onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="campaign-editor">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">{isEdit ? 'Modifier la campagne' : 'Nouvelle campagne email'}</DialogTitle>
                    <DialogDescription>
                        Variables de fusion : {'{{prenom}}, {{nom}}, {{nom_complet}}, {{civilite}}, {{fonction}}, {{organisation}}, {{pays}}'}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium text-foreground/70 block mb-1">Nom interne *</label>
                            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Invitation Sommet AEEN 2026" data-testid="campaign-name" />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-foreground/70 block mb-1">Objet de l'email *</label>
                            <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Ex: {{civilite}} {{nom}}, rejoignez le Sommet…" data-testid="campaign-subject" />
                        </div>
                    </div>

                    <AudienceBuilder filters={filters} setFilters={setFilters} api={api}
                                     lists={lists} onSaveList={saveList} />

                    <div className="flex items-center gap-3">
                        <Button variant="outline" size="sm" onClick={checkAudience} data-testid="campaign-check-audience">
                            <Users size={13} className="mr-1.5" /> Estimer l'audience
                        </Button>
                        {audience && (
                            <span className="text-sm" data-testid="campaign-audience-count">
                                <strong className="text-primary">{audience.count}</strong> destinataire{audience.count > 1 ? 's' : ''} avec email
                            </span>
                        )}
                    </div>

                    <div>
                        <label className="text-xs font-medium text-foreground/70 block mb-1">Contenu de l'email</label>
                        <RichTextEditor value={body} onChange={setBody} minHeight={220} placeholder="Bonjour {{civilite}} {{nom}}, …" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Fermer</Button>
                    <Button onClick={async () => { const s = await save(); if (s) onClose(); }} disabled={saving} data-testid="campaign-save">
                        {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />} Enregistrer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CampaignDetail({ campaignId, onClose, api }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/network/campaigns/${campaignId}`).then(r => setData(r.data)).catch(() => onClose());
    }, [campaignId, api, onClose]);

    const stats = useMemo(() => {
        const rs = data?.recipients || [];
        const sent = rs.filter(r => ['sent', 'opened', 'clicked'].includes(r.status)).length;
        const opened = rs.filter(r => ['opened', 'clicked'].includes(r.status)).length;
        const clicked = rs.filter(r => r.status === 'clicked').length;
        const unsub = rs.filter(r => r.status === 'unsubscribed').length;
        const failed = rs.filter(r => r.status === 'failed').length;
        return { total: rs.length, sent, opened, clicked, unsub, failed };
    }, [data]);

    return (
        <Dialog open onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="campaign-detail">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">{data?.campaign?.name || '…'}</DialogTitle>
                    <DialogDescription>{data?.campaign?.subject}</DialogDescription>
                </DialogHeader>
                {!data ? <div className="py-10 text-center"><Loader2 className="animate-spin inline" /></div> : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-4 gap-2">
                            {[['Envoyés', stats.sent], ['Ouvertures', stats.opened], ['Clics', stats.clicked], ['Désabos', stats.unsub]].map(([l, v]) => (
                                <div key={l} className="rounded-lg border border-border p-3 text-center">
                                    <div className="font-heading text-2xl">{v}</div>
                                    <div className="text-[11px] text-foreground/50">{l}{l !== 'Envoyés' && stats.sent > 0 ? ` · ${Math.round(v / stats.sent * 100)}%` : ''}</div>
                                </div>
                            ))}
                        </div>
                        {stats.failed > 0 && <div className="text-xs text-red-600">{stats.failed} échec(s) d'envoi</div>}
                        <div className="max-h-72 overflow-y-auto rounded-lg border border-border">
                            <table className="w-full text-sm">
                                <thead><tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-foreground/50">
                                    <th className="px-3 py-2">Destinataire</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2">Ouvert le</th>
                                </tr></thead>
                                <tbody>
                                    {(data.recipients || []).map(r => (
                                        <tr key={r.id} className="border-b border-border/50 last:border-0">
                                            <td className="px-3 py-2">
                                                <div className="font-medium">{r.network_contacts?.full_name || '—'}</div>
                                                <div className="text-[11px] text-foreground/50">{r.email}</div>
                                            </td>
                                            <td className="px-3 py-2 capitalize">{r.status}</td>
                                            <td className="px-3 py-2 text-xs">{r.opened_at ? new Date(r.opened_at).toLocaleString('fr-FR') : '—'}</td>
                                        </tr>
                                    ))}
                                    {(data.recipients || []).length === 0 && (
                                        <tr><td colSpan={3} className="px-3 py-6 text-center text-foreground/50">Aucun destinataire (campagne non envoyée)</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function NetworkCampaigns({ auth }) {
    const api = useMemo(() => axios.create({ baseURL: API, headers: auth?.headers || {} }), [auth]);
    const [items, setItems] = useState([]);
    const [smtpOk, setSmtpOk] = useState(true);
    const [loading, setLoading] = useState(true);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [detailId, setDetailId] = useState(null);
    const [lists, setLists] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/network/campaigns');
            setItems(r.data.items || []);
            setSmtpOk(!!r.data.smtp_configured);
        } catch { toast.error('Chargement impossible'); }
        finally { setLoading(false); }
    }, [api]);

    const loadLists = useCallback(async () => {
        try { setLists((await api.get('/network/lists')).data.items || []); } catch { /* silencieux */ }
    }, [api]);

    useEffect(() => { load(); loadLists(); }, [load, loadLists]);

    const sendTest = async (c) => {
        const email = window.prompt('Envoyer un email de test à :');
        if (!email?.trim()) return;
        try {
            await api.post(`/network/campaigns/${c.id}/test`, { email: email.trim() });
            toast.success('Email de test envoyé');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
    };

    const send = async (c) => {
        if (!window.confirm(`Envoyer la campagne « ${c.name} » à toute l'audience ciblée ? Cette action est irréversible.`)) return;
        try {
            const r = await api.post(`/network/campaigns/${c.id}/send`);
            toast.success(`Envoi lancé vers ${r.data.audience} destinataires`);
            setTimeout(load, 2000);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
    };

    const remove = async (c) => {
        if (!window.confirm(`Supprimer la campagne « ${c.name} » ?`)) return;
        try { await api.delete(`/network/campaigns/${c.id}`); toast.success('Supprimée'); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
    };

    return (
        <div className="space-y-5" data-testid="network-campaigns">
            {!smtpOk && (
                <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="campaigns-smtp-warning">
                    <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-sm text-amber-800">
                        <strong>Envoi désactivé — SMTP non configuré.</strong> Vous pouvez préparer vos campagnes (audience, contenu, variables), mais l'envoi nécessite des identifiants email (Gmail, SMTP pro ou Resend). Demandez l'activation quand vous êtes prêt.
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between">
                <Button onClick={() => { setEditing(null); setEditorOpen(true); }} data-testid="campaign-new-btn">
                    <Plus size={15} className="mr-1.5" /> Nouvelle campagne
                </Button>
                <div className="text-sm text-foreground/60">{items.length} campagne{items.length > 1 ? 's' : ''}</div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin" /></div>
            ) : items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border py-16 text-center text-foreground/50" data-testid="campaigns-empty">
                    Aucune campagne — créez votre première campagne ciblée vers vos 500+ contacts stratégiques.
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map(c => {
                        const b = STATUS_BADGES[c.status] || STATUS_BADGES.draft;
                        return (
                            <div key={c.id} className="rounded-xl border border-border bg-surface p-4 flex flex-wrap items-center gap-3" data-testid="campaign-row">
                                <div className="flex-1 min-w-[220px]">
                                    <div className="font-medium">{c.name}</div>
                                    <div className="text-xs text-foreground/50 truncate">{c.subject}</div>
                                </div>
                                <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${b.cls}`}>{b.label}</span>
                                <div className="flex items-center gap-3 text-xs text-foreground/60">
                                    <span className="inline-flex items-center gap-1"><Send size={11} /> {c.stats.sent}</span>
                                    <span className="inline-flex items-center gap-1"><MailOpen size={11} /> {c.stats.opened}</span>
                                    <span className="inline-flex items-center gap-1"><MousePointerClick size={11} /> {c.stats.clicked}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Button size="sm" variant="outline" onClick={() => setDetailId(c.id)} data-testid="campaign-view"><Eye size={13} /></Button>
                                    {['draft', 'scheduled'].includes(c.status) && (
                                        <>
                                            <Button size="sm" variant="outline" onClick={() => { setEditing(c); setEditorOpen(true); }} data-testid="campaign-edit"><Pencil size={13} /></Button>
                                            <Button size="sm" variant="outline" onClick={() => sendTest(c)} title="Envoyer un test" data-testid="campaign-test"><FlaskConical size={13} /></Button>
                                            <Button size="sm" onClick={() => send(c)} disabled={!smtpOk} data-testid="campaign-send"><Send size={13} className="mr-1" /> Envoyer</Button>
                                        </>
                                    )}
                                    {c.status !== 'sending' && (
                                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(c)} data-testid="campaign-delete"><Trash2 size={13} /></Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <CampaignEditor open={editorOpen} onClose={() => { setEditorOpen(false); load(); }} onSaved={() => load()}
                            api={api} campaign={editing} lists={lists} reloadLists={loadLists} />
            {detailId && <CampaignDetail campaignId={detailId} onClose={() => setDetailId(null)} api={api} />}
        </div>
    );
}
